import React, {
  useState,
  useEffect
} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Breadcrumbs,
  Link,
  Typography,
  Button,
  ButtonBase,
  IconButton,
  Grid,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  FormControlLabel,
  Checkbox,
  withStyles,
} from '@material-ui/core';

import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

import { shadeColor } from '../utils/shadeColor';

const useStyles = makeStyles((theme) => ({
  extraSubmitLinks: {
    marginTop: 8,
    marginBottom: 8
  },
  extraEmailsListContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'start',
    marginTop: 10,
    marginBottom: 10,
    '& > div': {
      marginRight: 10,
    }
  },
  extraEmailAddButtonContainer: {
    marginTop: 3,
  }
}));

const SaveButton = withStyles((theme) => ({
  root: {
    color: '#ffffff',
    backgroundColor: (window as any).APP_CONFIG ? (window as any).APP_CONFIG.siteColor1 : '#fd6422',
    '&:hover': {
      backgroundColor: shadeColor((window as any).APP_CONFIG ? (window as any).APP_CONFIG.siteColor1 : '#fd6422', 80),
    },
  },
}))(Button);


function validateEmail(email: string) {
  let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}


interface SaveFormProps {
  siteSetting: SiteSetting;
  design: Design;
  onClose: () => void;
  onSave: (design: DesignData) => void;
  isSaving: boolean;
  emailListEnabled: boolean;
}
const SaveForm = ({siteSetting, design, onClose, onSave, isSaving, emailListEnabled}: SaveFormProps) => {
  const classes = useStyles();
  const [name, setName] = useState((window as any)._customizer_user_name ? (window as any)._customizer_user_name : '');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState((window as any)._customizer_user_email ? (window as any)._customizer_user_email : '');
  const [emailError, setEmailError] = useState('');
  const [designName, setDesignName] = useState(design.design_name ? design.design_name : ((window as any)._customizer_design_name ? (window as any)._customizer_design_name : design.design_name));
  const [designNameError, setDesignNameError] = useState('');
  const [notes, setNotes] = useState((window as any)._customizer_user_notes ? (window as any)._customizer_user_notes : '');
  const [addToList, setAddToList] = useState(true);
  const [extraRecipients, setExtraRecipients] = useState([] as string[]);
  const [showExtraRecipientForm, setShowExtraRecipientForm] = useState(false);
  const [extraRecipientFieldContent, setExtraRecipientFieldContent] = useState('');
  const [extraRecipientFieldContentError, setExtraRecipientFieldContentError] = useState('');

  useEffect(() => {
    if (design.design_name && design.design_name != designName) setDesignName(design.design_name);
  }, [design]);

  const addExtraEmail = () => {
    if (!validateEmail(extraRecipientFieldContent)) {
      setExtraRecipientFieldContentError('Please enter a valid email address.');
      return false;
    }

    let recipients = Object.assign([], extraRecipients);
    recipients.push(extraRecipientFieldContent)
    setExtraRecipients(recipients);
    setShowExtraRecipientForm(false);
    setExtraRecipientFieldContent('');
    setExtraRecipientFieldContentError('');
    return true;
  }

  const doSave = (e: any) => {
    if (e && e.preventDefault) e.preventDefault();
    
    let hasError = false;
    if (!name) {
      setNameError('Please enter your name.');
      hasError = true;
    }
    else {
      setNameError('');
    }

    if (!designName) {
      setDesignNameError('Please enter a design name.');
      hasError = true;
    }
    else {
      setDesignNameError('');
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      hasError = true;
    }
    else {
      setEmailError('');
    }

    if (showExtraRecipientForm && extraRecipientFieldContent) {
      setExtraRecipientFieldContentError('Press "Confirm" or dismiss this field before you save your design.')
      hasError = true;
    }

    if (hasError) return;
    
    const designData: any = {
      design_name: designName,
      notes: notes,

      text_1: design.text_1,
      text_fill_color_1: design.text_fill_color_1,
      text_stroke_color_1: design.text_stroke_color_1,
      text_font_id_1: design.text_font_id_1,

      text_2: design.text_2,
      text_fill_color_2: design.text_fill_color_2,
      text_stroke_color_2: design.text_stroke_color_2,
      text_font_id_2: design.text_font_id_2,

      text_3: design.text_3,
      text_fill_color_3: design.text_fill_color_3,
      text_stroke_color_3: design.text_stroke_color_3,
      text_font_id_3: design.text_font_id_3,

      text_4: design.text_4,
      text_fill_color_4: design.text_fill_color_4,
      text_stroke_color_4: design.text_stroke_color_4,
      text_font_id_4: design.text_font_id_4,

      text_5: design.text_5,
      text_fill_color_5: design.text_fill_color_5,
      text_stroke_color_5: design.text_stroke_color_5,
      text_font_id_5: design.text_font_id_5,

      text_6: design.text_6,
      text_fill_color_6: design.text_fill_color_6,
      text_stroke_color_6: design.text_stroke_color_6,
      text_font_id_6: design.text_font_id_6,

      template: design.template,
      color_1: design.color_1,
      color_2: design.color_2,
      color_3: design.color_3,
      color_4: design.color_4,
      color_5: design.color_5,

      customer_name: name,
      customer_email: email,
      extra_recipients: extraRecipients,
    };

    if (addToList) designData.customer_add_to_list = true;

    if (design.subpart_1) designData.subpart_1 = design.subpart_1;
    if (design.subpart_2) designData.subpart_2 = design.subpart_2;
    if (design.subpart_3) designData.subpart_3 = design.subpart_3;
    if (design.subpart_4) designData.subpart_4 = design.subpart_4;
    if (design.subpart_5) designData.subpart_5 = design.subpart_5;

    if (design.logo_1_file) designData.logo_1_file = design.logo_1_file;
    if (design.logo_2_file) designData.logo_2_file = design.logo_2_file;
    if (design.logo_3_file) designData.logo_3_file = design.logo_3_file;
    if (design.logo_4_file) designData.logo_4_file = design.logo_4_file;
    if (design.logo_5_file) designData.logo_5_file = design.logo_5_file;

    let canvas = document.getElementById('design_generator') as HTMLCanvasElement;
    canvas.toBlob((image) => {
      designData.rendered_image = image;
      onSave(designData);
      (window as any)._customizer_user_name = name;
      (window as any)._customizer_user_notes = notes;
      (window as any)._customizer_user_email = email;
      (window as any)._customizer_team_name = designName;
    }, 'image/jpeg');

  }
  
  return (
    <>
      <DialogTitle id="save-dialog-title">Submit Your Design</DialogTitle>
      <DialogContent>

        <DialogContentText>
          {siteSetting.submit_message ? siteSetting.submit_message : 'Email your custom design to yourself and share with friends'}
        </DialogContentText>
        <form onSubmit={doSave}>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Your Name"
            variant="outlined"
            fullWidth
            value={name}
            onChange={(e: any) => setName(e.target.value.substring(0, 255))}
            error={Boolean(nameError)}
            helperText={nameError}
          />
          <TextField
            margin="dense"
            id="email"
            label="Your Email"
            type="email"
            variant="outlined"
            fullWidth
            value={email}
            onChange={(e: any) => setEmail(e.target.value.substring(0, 255))}
            error={Boolean(emailError)}
            helperText={emailError}
          />
          <TextField
            margin="dense"
            id="name"
            label="Your Design Name"
            variant="outlined"
            fullWidth
            value={designName}
            onChange={(e: any) => setDesignName(e.target.value.substring(0, 255))}
            error={Boolean(designNameError)}
            helperText={designNameError}
          />
          <TextField
            margin="dense"
            id="notes"
            label="Notes"
            placeholder="(Optional) Enter your notes"
            variant="outlined"
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e: any) => setNotes(e.target.value.substring(0, 255))}
          />
          <Button type="submit" style={{display: 'none'}}>Save</Button>
        </form>

        <div className={classes.extraSubmitLinks} dangerouslySetInnerHTML={{'__html': siteSetting.extra_submit_links}}>

        </div>

        <div className={classes.extraEmailsListContainer}>
          {extraRecipients.map((email: string, i: number) => (
            <Chip
              key={`extra-recipient-list-item-${i}`}
              label={email}
              onDelete={() => {
                let recipients = Object.assign([], extraRecipients);
                recipients.forEach((item, index) => {
                  if (item === email) recipients.splice(index,1);
                });
                setExtraRecipients(recipients);
              }}
            />
          ))}
        </div>

        <Collapse in={showExtraRecipientForm}>
          <form onSubmit={(e) => {
            e.preventDefault();
            addExtraEmail();
          }}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  margin="dense"
                  id="another-recipient-email"
                  label="Another Recipient's Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  value={extraRecipientFieldContent}
                  onChange={(e: any) => setExtraRecipientFieldContent(e.target.value.substring(0, 255))}
                  error={Boolean(extraRecipientFieldContentError)}
                  helperText={extraRecipientFieldContentError}
                />
              </Grid>
              <Grid item xs={6}>
                <div className={classes.extraEmailAddButtonContainer}>
                  <SaveButton
                    startIcon={<CheckIcon />}
                    color="primary"
                    type="submit"
                  >Confirm</SaveButton>
                  <IconButton
                    onClick={() => setShowExtraRecipientForm(false)}
                  ><CloseIcon /></IconButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </Collapse>
        <Collapse in={!showExtraRecipientForm}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setShowExtraRecipientForm(true)}
          >Add Another Recipient</Button>
        </Collapse>

        {!!emailListEnabled &&
        <FormControlLabel
          control={
            <Checkbox
              name="addToList"
              color="primary"
              checked={addToList}
              onChange={(e: any) => setAddToList(e.target.checked)}
            />
          }
          label="I want to receive emails with news and updates"
        />}

        <DialogContentText>
        Keep an eye on your inbox. Your new design will arrive at your inbox shortly.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          Cancel
        </Button>
        <SaveButton
          color="primary"
          onClick={doSave}
          disabled={isSaving}
          id="customizer-submit"
        >
          {isSaving ? "Loading..." : "Submit"}
        </SaveButton>
      </DialogActions>
    </>
  )
};


export default ({siteSetting, design, shown, onClose, onSave, isSaving, emailListEnabled}: any) => {
  const classes = useStyles();

  return (
    <Dialog onClose={onClose} aria-labelledby="save-dialog-title" open={shown} style={{zIndex: 999999}}>
      <SaveForm siteSetting={siteSetting} design={design} onClose={onClose} onSave={onSave} isSaving={isSaving} emailListEnabled={emailListEnabled} />
    </Dialog>
  )
}
